import React, { useState } from 'react';
import { NavLink, Link, Outlet, useLocation } from 'react-router-dom';
import { LayoutDashboard, FileText, Users, User, UserCircle, LogOut, Sun, Moon, Menu, X } from 'lucide-react';
import { UserProfile } from '../types';

interface DashboardLayoutProps {
  isDarkMode: boolean;
  toggleTheme: () => void;
  user: UserProfile;
  onLogout: () => void;
}

const SidebarItem: React.FC<{ to: string; icon: React.ReactNode; label: string; onClick?: () => void }> = ({ to, icon, label, onClick }) => {
  return (
    <NavLink
      to={to}
      end={to === '/dashboard'}
      onClick={onClick}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
          isActive
            ? 'bg-indigo-900/50 border-r-4 border-white text-white'
            : 'text-indigo-100 hover:bg-indigo-800 hover:text-white'
        }`
      }
    >
      {icon}
      <span>{label}</span>
    </NavLink>
  );
};

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({ isDarkMode, toggleTheme, user, onLogout }) => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const location = useLocation();

    const closeSidebar = () => setIsSidebarOpen(false);

  return (
    <div className="h-screen bg-gray-50 dark:bg-gray-900 flex transition-colors duration-200 overflow-hidden">
      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-20 md:hidden"
          onClick={closeSidebar}
        ></div>
      )}

      {/* Sidebar */}
      <aside 
        className={`w-56 bg-primary dark:bg-gray-800 text-white flex-shrink-0 fixed inset-y-0 left-0 z-30 transition-transform duration-300 ease-in-out md:translate-x-0 md:static md:inset-auto flex flex-col border-r dark:border-gray-700 ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="h-16 flex items-center justify-between px-4 bg-white dark:bg-white border-b border-gray-200 dark:border-gray-700 shrink-0">
           <Link to="/" className="flex items-center gap-2">
            <img 
              src={'/verify/logo.png'} 
              alt="Speedlink Logo" 
              className="w-19 h-8 object-contain" 
            />
                {/* <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-primary font-bold border-2 border-indigo-200">S</div>
                <div className="flex flex-col">
                  <span className="text-lg font-bold tracking-tight text-white">SPEEDLINK</span>
                  <span className="text-[8px] text-indigo-200 uppercase tracking-widest">Hi-Tech Solutions</span>
                </div> */}
            </Link>
            <button onClick={closeSidebar} className="md:hidden text-indigo-600 hover:text-indigo-800">
              <X className="w-5 h-5" />
            </button>
        </div>
        
        <nav className="flex-1 py-4 space-y-1 overflow-y-auto">
          <SidebarItem to="/dashboard" icon={<LayoutDashboard className="w-5 h-5" />} label="Dashboard" onClick={closeSidebar} />
          <SidebarItem to="/dashboard/certificates" icon={<FileText className="w-5 h-5" />} label="Certificates" onClick={closeSidebar} />
          
          {/* Only Show Admins Tab for Super Admin */}
          {user.role === 'SuperAdmin' && (
            <SidebarItem to="/dashboard/admins" icon={<Users className="w-5 h-5" />} label="Admins" onClick={closeSidebar} />
          )}
          
          <SidebarItem to="/dashboard/students" icon={<User className="w-5 h-5" />} label="Students" onClick={closeSidebar} />
          <SidebarItem to="/dashboard/profile" icon={<UserCircle className="w-5 h-5" />} label="Profile" onClick={closeSidebar} />
        </nav>

        <div className="p-4 border-t border-indigo-600 dark:border-gray-700 shrink-0">
          <button onClick={onLogout} className="w-full flex items-center gap-3 px-4 py-2 text-sm font-medium text-indigo-200 hover:text-white transition-colors">
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col w-full transition-all duration-200 h-full overflow-hidden">
        {/* Top Header */}
        <header className="h-16 bg-white dark:bg-gray-800 shadow-sm flex items-center justify-between px-4 md:px-8 border-b border-gray-100 dark:border-gray-700 transition-colors shrink-0">
            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsSidebarOpen(true)}
              className="md:hidden p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
            >
              <Menu className="w-6 h-6" />
            </button>

            <div className="flex items-center gap-4 ml-auto">
                <button 
                    onClick={toggleTheme}
                    className="p-2 rounded-full text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 transition-colors"
                >
                    {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                </button>
                <Link to="/dashboard/profile" className="flex items-center gap-3 pl-4 border-l border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg p-2 transition-colors">
                    <div className="text-right hidden sm:block">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{user.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{user.role}</p>
                    </div>
                    <div className="w-9 h-9 rounded-full bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center text-primary dark:text-indigo-200 font-bold text-sm">
                        {user.name.charAt(0).toUpperCase()}
                    </div>
                </Link>
            </div>
        </header>

        {/* Page Content */}
        <main className="p-4 md:p-8 overflow-y-auto flex-1 bg-gray-50 dark:bg-gray-900 scroll-smooth">
             <Outlet context={{ user }} />
        </main>
      </div>
    </div>
  );
};
